import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Value } from './screens/Value';
import { Earnings } from './screens/Earnings';
import { Radar } from './screens/Radar';
import { LeverageScreen } from './screens/LeverageScreen';
import { PositionDetail } from './screens/PositionDetail';
import { AddPosition } from './screens/AddPosition';
import { ReduceClose } from './screens/ReduceClose';
import { SettingsScreen } from './screens/Settings';
import { DividendsScreen } from './screens/Dividends';
import { TaxesScreen } from './screens/Taxes';
import { ContractDetail } from './screens/ContractDetail';
import { Results } from './screens/Results';
import { ActivityScreen } from './screens/Activity';
import { ValueDetails } from './screens/ValueDetails';
import type { RangeKey } from './state/series';
import type { ActivityFilter } from './state/activity';
import { StoreProvider, useStore } from './state/store';
import { PullToRefresh } from './components/PullToRefresh';
import { MexcAutoSync } from './components/MexcAutoSync';

export type Route =
  /* The tabs, plus the screens they lead to. Each is a real route, so the back
     gesture and the browser's history move between them the way they move
     anywhere else. */
  | { name: 'value' }
  | { name: 'leverage' }
  | { name: 'earnings'; symbol?: string }
  | { name: 'radar' }
  | { name: 'position'; ticker: string }
  | { name: 'add' }
  | { name: 'reduce'; ticker: string }
  | { name: 'settings'; group?: string }
  | { name: 'dividends' }
  | { name: 'taxes' }
  | { name: 'contract'; id: string }
  | { name: 'results' }
  | { name: 'activity'; query?: string; filter?: ActivityFilter }
  | { name: 'valueDetails'; range?: RangeKey; from?: string; to?: string };

export interface Nav {
  go: (route: Route) => void;
  back: () => void;
}

const ROUTE_NAMES = [
  'value', 'leverage', 'earnings', 'radar', 'position', 'add', 'reduce', 'settings', 'dividends',
  'taxes', 'contract', 'results', 'activity', 'valueDetails',
] as const;

/* History outlives a release. An entry written by the version that had separate
   Book and Holdings tabs still names them, and rendering an unknown route means
   rendering nothing — a blank screen on the first launch after an update, which
   is the worst possible moment for one. Anything unrecognised opens the book
   instead. */
function asRoute(value: unknown): Route {
  const name = (value as Route | undefined)?.name;
  return ROUTE_NAMES.includes(name as (typeof ROUTE_NAMES)[number])
    ? (value as Route)
    : { name: 'value' };
}

/* Navigation without a router dependency (§2). Routes live in history state so
   the Android back gesture behaves the way an installed app should. */
/** The screens the tab bar switches between. Moving among these is a crossfade
    in place; anything else is a step in or a step back. */
const TABS = new Set<Route['name']>(['value', 'leverage', 'earnings', 'radar', 'dividends', 'taxes']);

/* A screen change, animated where the platform can animate it.
 *
 * The browser photographs the page, the route changes underneath, and the two
 * pictures are moved by the rules in components.css under `data-nav`. React has
 * to finish the new screen inside the callback or the second picture is taken
 * of the old one, hence `flushSync`. Where the platform has no view transitions,
 * or the system has been asked for less motion, the change simply happens. */
function transition(kind: 'forward' | 'back' | 'tab', update: () => void) {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (typeof document.startViewTransition !== 'function' || reduced) { update(); return; }
  document.documentElement.dataset.nav = kind;
  const t = document.startViewTransition(() => flushSync(update));
  /* A transition the browser skips — the app sent to the background halfway
     through, a second tap landing before the first finished — still runs the
     update; only the animation is dropped. It reports that by rejecting `ready`,
     which is not a failure of anything here, so it is taken quietly rather than
     left as an unhandled error. */
  t.ready.catch(() => {});
}

function useNav(): [Route, Nav] {
  const [route, setRoute] = useState<Route>(() => asRoute(history.state?.route));
  // Read by the handlers below, which are made once and would otherwise only
  // ever see the first route.
  const current = useRef(route);
  current.current = route;

  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const next = asRoute(e.state?.route);
      const kind = TABS.has(next.name) && TABS.has(current.current.name) ? 'tab' : 'back';
      transition(kind, () => setRoute(next));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const go = useCallback((next: Route) => {
    history.pushState({ route: next }, '');
    const kind = TABS.has(next.name) && TABS.has(current.current.name) ? 'tab' : 'forward';
    transition(kind, () => { setRoute(next); window.scrollTo(0, 0); });
  }, []);

  const back = useCallback(() => history.back(), []);

  return [route, { go, back }];
}

function Shell() {
  const [route, nav] = useNav();
  const { state } = useStore();

  if (!state.ready) {
    // The first paint comes from IndexedDB, so this is only ever a flash.
    return <div className="empty" style={{ paddingTop: 80 }}>Opening your book…</div>;
  }

  switch (route.name) {
    case 'value': return <Value nav={nav} />;
    case 'leverage': return <LeverageScreen nav={nav} />;
    case 'earnings': return route.symbol
      ? <Earnings nav={nav} initialSymbol={route.symbol} />
      : <Earnings nav={nav} />;
    case 'radar': return <Radar nav={nav} />;
    case 'position': return <PositionDetail ticker={route.ticker} nav={nav} />;
    case 'add': return <AddPosition nav={nav} />;
    case 'reduce': return <ReduceClose ticker={route.ticker} nav={nav} />;
    case 'settings': return route.group
      ? <SettingsScreen nav={nav} group={route.group} />
      : <SettingsScreen nav={nav} />;
    case 'dividends': return <DividendsScreen nav={nav} />;
    case 'taxes': return <TaxesScreen nav={nav} />;
    case 'contract': return <ContractDetail id={route.id} nav={nav} />;
    case 'results': return <Results nav={nav} />;
    case 'activity': return (
      <ActivityScreen nav={nav}
                      {...(route.query ? { initialQuery: route.query } : {})}
                      {...(route.filter ? { initialFilter: route.filter } : {})} />
    );
    case 'valueDetails': return (
      <ValueDetails nav={nav}
                    {...(route.range ? { range: route.range } : {})}
                    {...(route.from ? { from: route.from } : {})}
                    {...(route.to ? { to: route.to } : {})} />
    );
  }
}

export function App() {
  return (
    <StoreProvider>
      <MexcAutoSync />
      <PullToRefresh>
        <Shell />
      </PullToRefresh>
    </StoreProvider>
  );
}
